import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  X,
  Image as ImageIcon,
  FileWarning,
  Loader2,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { validateFile, formatFileSize, MAX_FILE_SIZE, ALLOWED_TYPES } from '../../services/mediaService';

/**
 * Drag-and-drop image uploader with progress, preview, and client-side validation.
 *
 * @param {{
 *   onUpload: (file: File) => Promise<void>,
 *   accept?: string,
 *   maxSize?: number,
 *   className?: string,
 *   compact?: boolean,
 *   currentImageUrl?: string,
 *   label?: string,
 * }} props
 */
export function ImageUploader({
  onUpload,
  accept = ALLOWED_TYPES.join(','),
  maxSize = MAX_FILE_SIZE,
  className,
  compact = false,
  currentImageUrl,
  label = 'Upload Image',
}) {
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setPreview(null);
    setSelectedFile(null);
    setProgress(0);
    setError('');
    setSuccess(false);
  };

  const handleFile = useCallback((file) => {
    setError('');
    setSuccess(false);

    const check = validateFile(file);
    if (!check.valid) {
      setError(check.error);
      return;
    }

    // Preview
    const url = URL.createObjectURL(file);
    setPreview(url);
    setSelectedFile(file);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!selectedFile || !onUpload) return;
    setUploading(true);
    setError('');
    setProgress(0);

    try {
      await onUpload(selectedFile, (pct) => setProgress(pct));
      setSuccess(true);
      // Keep preview visible for a moment
      setTimeout(reset, 1500);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const displayImage = preview || currentImageUrl;

  /* ── Compact variant (for product forms) ── */
  if (compact) {
    return (
      <div className={clsx('space-y-2', className)}>
        <div className="flex items-center gap-4">
          {/* Thumbnail */}
          <div className="w-20 h-20 rounded-xl overflow-hidden bg-brand-light border border-ui-border flex-shrink-0">
            {displayImage ? (
              <img src={displayImage} alt="Preview" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ImageIcon size={24} className="text-ui-muted" />
              </div>
            )}
          </div>

          <div className="flex-1 space-y-2">
            {/* Upload/replace button */}
            <label className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-brand-dark bg-brand-light hover:bg-gray-200 rounded-xl cursor-pointer transition-colors">
              <Upload size={14} />
              {currentImageUrl ? 'Replace' : 'Choose File'}
              <input
                ref={inputRef}
                type="file"
                accept={accept}
                onChange={handleInputChange}
                className="hidden"
              />
            </label>

            {selectedFile && !uploading && !success && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-brand-gray truncate max-w-[140px]">
                  {selectedFile.name}
                </span>
                <span className="text-[10px] text-ui-muted">
                  ({formatFileSize(selectedFile.size)})
                </span>
                <button
                  type="button"
                  onClick={handleUpload}
                  className="text-xs font-bold text-brand-white bg-brand-red hover:bg-red-700 px-3 py-1 rounded-lg transition-colors"
                >
                  Upload
                </button>
                <button
                  type="button"
                  onClick={reset}
                  className="p-1 rounded text-brand-gray hover:text-error transition-colors"
                >
                  <X size={12} />
                </button>
              </div>
            )}

            {/* Progress */}
            {uploading && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin text-brand-red" />
                  <span className="text-xs font-medium text-brand-gray">
                    Uploading… {progress}%
                  </span>
                </div>
                <div className="w-full h-1.5 bg-brand-light rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    className="h-full bg-brand-red rounded-full"
                    transition={{ duration: 0.2 }}
                  />
                </div>
              </div>
            )}

            {success && (
              <span className="flex items-center gap-1 text-xs text-success font-bold">
                <CheckCircle size={14} /> Uploaded!
              </span>
            )}
          </div>
        </div>

        {/* Validation hints */}
        <p className="text-[10px] text-ui-muted">
          Max 5 MB · JPG, PNG, WebP, GIF, SVG
        </p>

        {error && (
          <div className="flex items-center gap-1.5 text-xs text-error">
            <AlertCircle size={12} />
            {error}
          </div>
        )}
      </div>
    );
  }

  /* ── Full drag-and-drop variant ── */
  return (
    <div className={clsx('space-y-3', className)}>
      {label && (
        <label className="block text-sm font-semibold text-brand-dark">{label}</label>
      )}

      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !uploading && inputRef.current?.click()}
        className={clsx(
          'relative border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all',
          dragOver
            ? 'border-brand-red bg-red-50/50'
            : 'border-ui-border hover:border-brand-red/40 hover:bg-brand-light/50',
          uploading && 'pointer-events-none opacity-70'
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleInputChange}
          className="hidden"
        />

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-2 py-4"
            >
              <CheckCircle size={36} className="text-success" />
              <p className="text-sm font-bold text-success">Upload complete!</p>
            </motion.div>
          ) : uploading ? (
            <motion.div
              key="uploading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-3 py-4"
            >
              <Loader2 size={36} className="animate-spin text-brand-red" />
              <p className="text-sm font-bold text-brand-dark">Uploading… {progress}%</p>
              <div className="w-48 h-2 bg-brand-light rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  className="h-full bg-brand-red rounded-full"
                  transition={{ duration: 0.2 }}
                />
              </div>
            </motion.div>
          ) : preview ? (
            <motion.div
              key="preview"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-3"
            >
              <div className="w-32 h-32 rounded-xl overflow-hidden border border-ui-border">
                <img src={preview} alt="Preview" className="w-full h-full object-cover" />
              </div>
              <div className="text-center">
                <p className="text-sm font-bold text-brand-dark truncate max-w-[200px]">
                  {selectedFile?.name}
                </p>
                <p className="text-xs text-brand-gray">
                  {formatFileSize(selectedFile?.size)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUpload();
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-brand-white bg-brand-red hover:bg-red-700 rounded-xl transition-colors"
                >
                  <Upload size={14} />
                  Upload
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    reset();
                  }}
                  className="px-4 py-2 text-sm font-bold text-brand-gray bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-2 py-4"
            >
              <div className="w-12 h-12 rounded-xl bg-brand-light flex items-center justify-center">
                <Upload size={22} className="text-brand-gray" />
              </div>
              <div className="text-center">
                <p className="text-sm font-bold text-brand-dark">
                  Drop image here or <span className="text-brand-red">browse</span>
                </p>
                <p className="text-xs text-brand-gray mt-1">
                  JPG, PNG, WebP, GIF, SVG · Max 5 MB
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Error message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="flex items-center gap-2 p-3 bg-red-50 border border-error/20 rounded-xl"
          >
            <FileWarning size={16} className="text-error flex-shrink-0" />
            <span className="text-sm text-error">{error}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
